import React from 'react';
import Header from '@amzn/awsui-components-console/header';
import Table from '@amzn/awsui-components-console/table';
import Box from '@amzn/awsui-components-console/box';
import Alert from '@amzn/awsui-components-console/alert';
import { ApolloError } from '@apollo/client';
import { Translation } from 'src/generated/graphql';
import { TranslationStatusIndicator } from 'src/common/translationStatus';
import {
  TablePreferences,
  useTablePreferences,
} from 'src/common/ui/TablePreferences';
import { localesTablePreferences } from './stringTablePreferences';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface LocalesTableProps {
  translations: Translation[];
  loading: boolean;
  error?: ApolloError;
}

const LocalesTable: React.FC<LocalesTableProps> = ({
  translations,
  loading,
  error,
}) => {
  const { t } = useTranslation();
  // Columns only: every locale renders, unpaged.
  const { preferences, onConfirm, columnDisplay } = useTablePreferences(
    localesTablePreferences(t),
  );

  if (error) {
    return (
      <Alert type="error" header={t('string-locales-error')}>
        {error.message}
      </Alert>
    );
  }

  return (
    <Table
      columnDisplay={columnDisplay}
      ariaLabels={{ tableLabel: t('string-locales-table-aria') }}
      wrapLines={preferences.wrapLines}
      stripedRows={preferences.stripedRows}
      contentDensity={preferences.contentDensity}
      preferences={
        <TablePreferences
          config={localesTablePreferences(t)}
          preferences={preferences}
          onConfirm={onConfirm}
        />
      }
      header={
        <Header
          counter={
            !loading && translations.length > 0
              ? `(${translations.length})`
              : undefined
          }
        >
          {t('string-locales-heading')}
        </Header>
      }
      loading={loading}
      loadingText={t('string-locales-loading')}
      items={translations}
      columnDefinitions={[
        {
          id: 'locale',
          header: t('string-column-locale'),
          cell: (item) => (
            <span data-testid="locale-row" data-locale={item.locale}>
              <span data-testid="locale-code">{item.locale}</span>
            </span>
          ),
        },
        {
          id: 'status',
          header: t('string-column-status'),
          cell: (item) => (
            <span data-testid="locale-status-badge">
              <TranslationStatusIndicator status={item.status} />
            </span>
          ),
        },
        {
          id: 'translatedText',
          header: t('string-column-translation'),
          cell: (item) => (
            <span data-testid="locale-translation">{item.translatedText}</span>
          ),
        },
      ]}
      empty={
        <Box textAlign="center" color="text-status-inactive" padding="l">
          {t('string-locales-empty')}
        </Box>
      }
    />
  );
};

export default LocalesTable;
